import { useEffect, useState } from "react";
import mark from "../assets/stellar-siege-mark.svg";
import { signInWithEmail, signUpWithEmail } from "../services/firebase/authService";
import { checkUsernameAvailability, generateUniqueUsername } from "../services/firebase/profileService";
import { Button } from "./Button";

export function AuthGate({ configured, loading }) {
	const [mode, setMode] = useState("login");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [username, setUsername] = useState("");
	const [busy, setBusy] = useState(false);
	const [message, setMessage] = useState("");
	const [usernameStatus, setUsernameStatus] = useState({ tone: "muted", message: "" });

	useEffect(() => {
		if (!configured || mode !== "signup") return undefined;

		const trimmed = username.trim();
		if (!trimmed) {
			setUsernameStatus({ tone: "muted", message: "Choose a username or generate one." });
			return undefined;
		}

		let cancelled = false;
		setUsernameStatus({ tone: "muted", message: "Checking username..." });
		const timeout = window.setTimeout(() => {
			checkUsernameAvailability(trimmed)
				.then((result) => {
					if (cancelled) return;
					setUsernameStatus(result.ok
						? { tone: "success", message: "Username available." }
						: { tone: "warn", message: result.reason });
				})
				.catch(() => {
					if (!cancelled) setUsernameStatus({ tone: "warn", message: "Could not verify username right now." });
				});
		}, 280);

		return () => {
			cancelled = true;
			window.clearTimeout(timeout);
		};
	}, [configured, mode, username]);

	const assignGeneratedUsername = async () => {
		setUsernameStatus({ tone: "muted", message: "Generating username..." });
		try {
			const generated = await generateUniqueUsername();
			setUsername(generated);
			setUsernameStatus({ tone: "success", message: "Generated unique username." });
		} catch (error) {
			setUsernameStatus({ tone: "warn", message: error.message || "Could not generate a username right now." });
		}
	};

	const submit = async (event) => {
		event.preventDefault();
		setBusy(true);
		setMessage("");

		try {
			if (mode === "login") await signInWithEmail(email, password);
			else {
				const availability = await checkUsernameAvailability(username.trim());
				if (!availability.ok) throw new Error(availability.reason);
				await signUpWithEmail(email, password, username);
			}
			setPassword("");
		} catch (error) {
			setMessage(error.message || "Authentication failed.");
		} finally {
			setBusy(false);
		}
	};

	return (
		<div className="auth-gate" role="dialog" aria-modal="true" aria-labelledby="auth-gate-title">
			<section className="auth-gate__panel">
				<img src={mark} alt="" className="auth-gate__mark" />
				<div>
					<div className="eyebrow">Commander Access</div>
					<h1 id="auth-gate-title">Stellar Siege</h1>
					<p>Sign in to play, sync progress, and keep campaign saves tied to your account.</p>
				</div>

				{loading && <div className="auth-gate__status">Checking command identity...</div>}

				{!loading && !configured && (
					<div className="auth-gate__status auth-gate__status--warn">
						Firebase login is not configured for this build.
					</div>
				)}

				{!loading && configured && (
					<>
						<div className="auth-gate__tabs">
							<button type="button" className={mode === "login" ? "is-active" : ""} onClick={() => setMode("login")}>Login</button>
							<button type="button" className={mode === "signup" ? "is-active" : ""} onClick={() => setMode("signup")}>Create Account</button>
						</div>
						<form onSubmit={submit} className="auth-gate__form">
							<label>
								<span>Email</span>
								<input value={email} onChange={(event) => setEmail(event.target.value)} type="email" placeholder="you@example.com" autoComplete="email" required />
							</label>
							<label>
								<span>Password</span>
								<div className="password-row">
									<input
										value={password}
										onChange={(event) => setPassword(event.target.value)}
										type={showPassword ? "text" : "password"}
										placeholder="At least 6 characters"
										autoComplete={mode === "login" ? "current-password" : "new-password"}
										minLength={6}
										required
									/>
									<button
										type="button"
										className="password-toggle"
										aria-label={showPassword ? "Hide password" : "Show password"}
										aria-pressed={showPassword}
										onClick={() => setShowPassword((current) => !current)}
									>
										{"\u{1F441}"}
									</button>
								</div>
							</label>
							{mode === "signup" && (
								<label>
									<span>Username</span>
									<div className="username-row">
										<input value={username} onChange={(event) => setUsername(event.target.value)} type="text" placeholder="SpicyCactus816" autoComplete="nickname" minLength={4} maxLength={20} required />
										<Button type="button" tone="ghost" onClick={assignGeneratedUsername} disabled={busy}>Random</Button>
									</div>
									<small className={`field-status field-status--${usernameStatus.tone}`}>{usernameStatus.message}</small>
								</label>
							)}
							<Button type="submit" tone="success" disabled={busy || (mode === "signup" && usernameStatus.tone === "warn")}>{busy ? "Working..." : mode === "login" ? "Login" : "Create Account"}</Button>
						</form>
						{message && <p className="form-error">{message}</p>}
						<p className="auth-gate__links">
							<a href="/account-deletion/" target="_blank" rel="noopener">Request account deletion</a>
							<span> / </span>
							<a href="/privacy/" target="_blank" rel="noopener">Privacy policy</a>
						</p>
					</>
				)}
			</section>
		</div>
	);
}
